import React from 'react';

/**
 * Off-screen, invisible, inert render of the chat panel's parts, mounted once during idle time
 * while the launcher is closed and removed right after the browser has laid it out.
 *
 * The first open of the panel is dominated by one-off costs rather than by the panel itself:
 * compiling the panel's component code on first call, resolving the widget's font stack for
 * the first text it lays out, and first-time style matching. Paying those in an idle period
 * keeps them out of the launcher click (interaction to next paint) and leaves the DOM exactly
 * as it was, so the real panel, its ids, focus handling and modality are unchanged.
 *
 * While mounted the subtree is `visibility: hidden` (not rendered, not focusable, not in the
 * accessibility tree), additionally `aria-hidden` and `inert`, zero-sized and clipped, and
 * fixed-positioned so it neither shifts nor scrolls the host page.
 */
const WRAPPER_STYLE: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  width: 0,
  height: 0,
  overflow: 'hidden',
  visibility: 'hidden',
  pointerEvents: 'none',
};

/** Same box as the floating panel so text wraps and lays out as it will when opened. */
const FRAME_STYLE: React.CSSProperties = { width: 384, height: 600 };

interface PanelWarmupProps {
  children: React.ReactNode;
  /** Called once the subtree has been through a layout; the parent then unmounts it. */
  onDone: () => void;
  /** Class list of the real panel frame, so the same style rules apply. */
  frameClassName: string;
}

export default function PanelWarmup({ children, onDone, frameClassName }: PanelWarmupProps) {
  const setInert = React.useCallback((node: HTMLDivElement | null) => {
    // Set as a DOM attribute: React 18 has no `inert` prop and React 19 treats "" as false.
    if (node) node.setAttribute('inert', '');
  }, []);

  React.useEffect(() => {
    // The frame after this commit lays the subtree out (fonts resolved, styles matched); a task
    // queued from that frame's rAF runs after it, so the warm-up is complete by then.
    let timer: ReturnType<typeof setTimeout> | null = null;
    const raf =
      typeof requestAnimationFrame === 'function'
        ? requestAnimationFrame(() => {
            timer = setTimeout(onDone, 0);
          })
        : null;
    if (raf === null) timer = setTimeout(onDone, 0);
    return () => {
      if (raf !== null && typeof cancelAnimationFrame === 'function') cancelAnimationFrame(raf);
      if (timer) clearTimeout(timer);
    };
  }, [onDone]);

  return (
    <div ref={setInert} aria-hidden="true" data-rw-panel-warmup="true" style={WRAPPER_STYLE}>
      <div className={frameClassName} style={FRAME_STYLE}>
        {children}
      </div>
    </div>
  );
}
